/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { TermAllReq } from './term-all-req'
import { Term } from './term'
import { TermReq } from './term-req'
import { QueryTermsQuery } from './query-terms-query'

const buildConfig = createConfigBuilder('growp')

export const termApi = {
  queryAllTermsConfig(
    termAllReq: TermAllReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/term/terms/all',
      {},
      null,
      termAllReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 查询学校指定领域下词汇
   * @param termAllReq
   * @param [options]
   * @return AxiosResponsePromise<Term>
   */
  queryAllTerms(
    termAllReq: TermAllReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Term> {
    return axios(termApi.queryAllTermsConfig(termAllReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Term(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryAllTerms(
    termAllReq: TermAllReq,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Term> {
    return useQueryApi<Term>(
      termApi.queryAllTerms,
      'Term.queryAllTerms',
      queryOptions,
      termAllReq
    )
  },

  queryTermsConfig(
    type: string,
    queryTermsQuery?: QueryTermsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/term/{type}/terms',
      {
        type,
      },
      queryTermsQuery
    )
  },

  /**
   * @summary 查询词汇
   * @param type
   * @param [queryTermsQuery]
   * @param [queryTermsQuery.name]
   * @param [queryTermsQuery.parentId]
   * @param [queryTermsQuery.schoolId]
   * @param [queryTermsQuery.paginated] 是否分页，默认 1
   * @param [queryTermsQuery.page] 显示的数据页，默认 1
   * @param [queryTermsQuery.perPage] 每页显示数量，默认 20
   * @param [queryTermsQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<Term[]>
   */
  queryTerms(
    type: string,
    queryTermsQuery?: QueryTermsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Term[]> {
    return axios(termApi.queryTermsConfig(type, queryTermsQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new Term(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryTerms(
    {
      type,
      query,
    }: {
      type: string
      query?: QueryTermsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Term[]> {
    return useQueryApi<Term[]>(
      termApi.queryTerms,
      'Term.queryTerms',
      queryOptions,
      type,
      query
    )
  },

  createTermConfig(
    type: string,
    termReq: TermReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/term/{type}/terms',
      {
        type,
      },
      null,
      termReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          id: (v: any) => v || 0,
          description: (v: any) => v || '',
          parentId: (v: any) => v || 0,
          schoolId: (v: any) => v || 0,
          weight: (v: any) => v || 0,
          deletedAt: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 创建词汇
   * @param type
   * @param termReq
   * @param [options]
   * @return AxiosResponsePromise<Term>
   */
  createTerm(
    type: string,
    termReq: TermReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Term> {
    return axios(termApi.createTermConfig(type, termReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Term(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCreateTerm(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ type, payload }: { type: string; payload: TermReq }) =>
        termApi.createTerm(type, payload),
      mutationOptions
    )
  },

  deleteTermConfig(
    type: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/term/{type}/terms/{id}', {
      type,
      id,
    })
  },

  /**
   * @summary 删除词汇
   * @param type
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteTerm(
    type: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(termApi.deleteTermConfig(type, id, options)) as any
  },

  useDeleteTerm(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ type, id }: { type: string; id: number }) =>
        termApi.deleteTerm(type, id),
      mutationOptions
    )
  },

  loadTermConfig(
    type: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/term/{type}/terms/{id}', {
      type,
      id,
    })
  },

  /**
   * @summary 查询单个词汇
   * @param type
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<Term>
   */
  loadTerm(
    type: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Term> {
    return axios(termApi.loadTermConfig(type, id, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Term(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLoadTerm(
    {
      type,
      id,
    }: {
      type: string
      id: number
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Term> {
    return useQueryApi<Term>(
      termApi.loadTerm,
      'Term.loadTerm',
      queryOptions,
      type,
      id
    )
  },

  updateTermConfig(
    type: string,
    id: number,
    termReq: TermReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/term/{type}/terms/{id}',
      {
        type,
        id,
      },
      null,
      termReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          id: (v: any) => v || 0,
          description: (v: any) => v || '',
          parentId: (v: any) => v || 0,
          schoolId: (v: any) => v || 0,
          weight: (v: any) => v || 0,
          deletedAt: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 更新词汇
   * @param type
   * @param id
   * @param termReq
   * @param [options]
   * @return AxiosResponsePromise<Term>
   */
  updateTerm(
    type: string,
    id: number,
    termReq: TermReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Term> {
    return axios(termApi.updateTermConfig(type, id, termReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Term(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useUpdateTerm(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ type, id, payload }: { type: string; id: number; payload: TermReq }) =>
        termApi.updateTerm(type, id, payload),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(termApi).forEach((api) => {
  // @ts-ignore
  termApi[api]._name_ = `Term.${api}`
})
